"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";
import { BlogSearch } from "@/components/global/partials/BlogSearch";
import Categories from "@/components/global/partials/Categories";

import globe from "@/public/global/globe.svg";
import logo from "@/public/global/logo_full.svg";
import logoBlack from "@/public/global/logo_sm_black.svg";
import IconMobileMenuClosed from "@/public/global/menu.svg";

const IconMobileMenuOpen = () => (
  <svg
    width="33"
    height="25"
    viewBox="0 0 33 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.83252 0.598389L28.167 23.9329"
      stroke="black"
      strokeWidth="1.69231"
    />
    <path
      d="M4.83252 23.9329L28.167 0.598338"
      stroke="black"
      strokeWidth="1.69231"
    />
  </svg>
);

export default function Header({
  categories,
  activeCategory,
  hideMenu = false,
}: {
  categories: string[];
  activeCategory: string;
  hideMenu?: boolean;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isMounted, setIsMounted] = React.useState(false);
  const [mobileMenuContainer, setMobileMenuContainer] =
    React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    setIsMounted(true);
    if (typeof document !== "undefined") {
      const portal = document.createElement("div");
      portal.id = "mobile-menu";
      portal.style.position = "fixed";
      portal.style.top = "0";
      portal.style.left = "0";
      portal.style.width = "100%";
      portal.style.height = "100%";
      portal.style.zIndex = "100";
      portal.style.transform = "translateX(100%)";
      document.body.appendChild(portal);
      setMobileMenuContainer(portal);
    }

    return () => {
      setIsMounted(false);
      const portal = document.getElementById("mobile-menu");
      if (portal) {
        portal.remove();
      }
      setMobileMenuContainer(null);
    };
  }, []);

  const dateFull = React.useMemo(() => {
    return new Date().toLocaleDateString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  return (
    <header className={cn("w-full bg-white h-[17rem]", hideMenu ? "lg:h-[15rem]" : "lg:h-[20rem]")}>
      <div className={cn("w-full h-full", !hideMenu && "lg:h-[65%]")}>
        <div className="w-full h-full max-w-[224rem] mx-auto lg:px-0 px-[2rem] flex flex-col lg:flex-row justify-center gap-[3rem] lg:justify-between items-center">
          <div className="w-full flex justify-between items-center">
            <div className="hidden lg:flex w-full items-center gap-[1rem]">
              <Image
                src={globe}
                alt="globe"
                className="w-[3rem] min-w-[3rem] h-[3rem] min-h-[3rem]"
              />
              <span className="">{dateFull}</span>
            </div>
            {/*  */}
            <Link href={"/"} className="w-full">
              <Image
                src={logo}
                alt="logo"
                className="w-[33rem] lg:w-[41rem] lg:mx-auto"
              />
            </Link>
            {/*  */}
            <div className="hidden lg:flex w-full items-center gap-[1rem] justify-end">
              <BlogSearch />
              <button className="hidden px-[3rem] lg:flex items-center gap-[0.75rem] justify-center bg-[#D8E8EF]/40 hover:bg-[#D8E8EF]/60 transition-colors h-[6rem] min-h-[6rem] rounded-full">
                Sign in
              </button>
              <button className="px-[3rem] bg-[#D8E8EF]/40 hover:bg-[#D8E8EF]/60 transition-colors h-[6rem] min-h-[6rem] rounded-full text-center flex gap-[1rem] items-center justify-center">
                <Image
                  src={logoBlack}
                  alt="logo"
                  className="w-[1.75rem] min-w-[1.75rem] max-w-[1.75rem]"
                />
                Subscribe
              </button>
            </div>
            {/* mobile menu btn */}
            <button
              className="lg:hidden flex-shrink-0 relative"
              onClick={() => {
                setIsMobileMenuOpen(true);
              }}
            >
              <Image
                src={IconMobileMenuClosed}
                alt="Mobile menu button"
                className="!w-[4.875rem] !min-w-[4.875rem]"
              />
            </button>
            {/* end mobile menu btn */}
          </div>

          {/* search mobile */}
          <div className="w-full lg:hidden">
            <BlogSearch />
          </div>
          {/* end search mobile */}
        </div>
      </div>
      {/*  */}
      {!hideMenu && (
        <div className="hidden lg:block w-full h-[35%] border-y border-y-black/10">
          <div className="w-full h-full max-w-[224rem] mx-auto lg:px-0 px-[2rem] flex justify-center items-center">
            <Categories categories={categories} activeCategory={activeCategory} />
          </div>
        </div>
      )}
      {/* mobile menu */}
      {mobileMenuContainer &&
        createPortal(
          <>
            <div
              className={cn(
                "fixed lg:hidden z-[80] w-full h-full pb-[4rem] transition-all duration-300 ease-out bg-white overflow-y-auto flex flex-col justify-between",
                isMobileMenuOpen ? "-translate-x-full" : "translate-x-0"
              )}
            >
              <div>
                <div className="container flex flex-row justify-between items-center mb-[6rem] pt-[2rem]">
                  <Link href={"/"}>
                    <Image src={logo} alt="logo" className="w-[33rem]" />
                  </Link>

                  <button
                    className="flex items-center justify-center pe-2"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <div className="w-[4rem] h-[4rem]">
                      <IconMobileMenuOpen />
                    </div>
                  </button>
                </div>

                <div className="container flex flex-col justify-start items-stretch">
                  <Categories
                    categories={categories}
                    activeCategory={activeCategory}
                  />
                </div>
              </div>

              <div className="container flex flex-col w-full gap-[1rem] mt-[4rem]">
                <button className="px-[3rem] flex items-center gap-[0.75rem] justify-center bg-[#D8E8EF]/40 hover:bg-[#D8E8EF]/60 transition-colors h-[6rem] min-h-[6rem] rounded-full">
                  Sign in
                </button>
                <button className="px-[3rem] bg-[#D8E8EF]/40 hover:bg-[#D8E8EF]/60 transition-colors h-[6rem] min-h-[6rem] rounded-full text-center flex gap-[1rem] items-center justify-center">
                  <Image src={logoBlack} alt="logo" className="w-[1.75rem] min-w-[1.75rem] max-w-[1.75rem]" />
                  Subscribe
                </button>
              </div>
            </div>
          </>,
          mobileMenuContainer,
          "mobile-menu"
        )}
      {/* end mobile menu */}
    </header>
  );
}
